import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  X, Settings, Video, Mic, Volume2, Image, Sparkles,
  Loader2, Keyboard, BarChart3, Play, ChevronDown, Check,
  Wand2, Upload, MonitorPlay
} from 'lucide-react';
import { VirtualBackgroundProcessor } from '@videosdk.live/videosdk-media-processor-web';
import { backgroundImageUrls } from '../../../utils/background';
import { getRoomVirtualBackgrounds, uploadVirtualBackground, deleteVirtualBackground } from '../../../services/virtualBackgroundServices';
import testSoundFile from '../../../sounds/test_sound.wav';
import { useMeetingState } from '../../../context/MeetingStateContext';

const SETTINGS_TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'camera', label: 'Camera', icon: Video },
  { id: 'audio', label: 'Audio', icon: Mic },
  { id: 'visual', label: 'Visual effects', icon: Image },
  // { id: 'shortcuts', label: 'Hotkeys', icon: Keyboard },
  { id: 'stats', label: 'Statistics', icon: BarChart3 },
];

// Toggle Switch Component
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={!!checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// Slider Component
function Slider({ value, onChange, min = 0, max = 100, step = 1 }) {
  return (
    <input
      type="range"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      min={min}
      max={max}
      step={step}
      className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#6a1fbf]"
    />
  );
}

export default function SettingsModal({
  isOpen,
  onClose,
  // Device state
  audioInputs = [],
  videoInputs = [],
  audioOutputs = [],
  selectedAudioInput,
  selectedVideoInput,
  selectedAudioOutput,
  onAudioInputChange,
  onVideoInputChange,
  onAudioOutputChange,
  // Room context for virtual backgrounds
  roomId,
  // Virtual background props
  blurEnabled = false,
  setBlurEnabled,
  blurStrength = 50,
  setBlurStrength,
  selectedBackground = null,
  handleStartVirtualBackground,
  virtualBackgroundOn = false,
  // Green screen props
  greenScreenEnabled: greenScreenEnabledProp = false,
  handleStartGreenScreen,
  // Local webcam stream for preview
  localWebcamStream,
  localWebcamOn = false,
  processedWebcamStream = null,
  // Meeting stats props (ref to avoid parent re-renders)
  meetingStats = null,
  localParticipantStatsRef = null,
  // Camera resolution callback
  onCameraResolutionChange,
  // Audio processing callbacks
  onAudioSettingsChange,
  // AI Noise cancellation props
  noiseCancellationEnabled = true,
  noiseCancellationMode = 'NSNG',
  onNoiseCancellationToggle,
  onNoiseCancellationModeChange,
  // Host controls
  meetingRole,
  waitingRoomEnabled = false,
  onWaitingRoomChange,
  autoAdmitRegistered = false,
  onAutoAdmitRegisteredChange,
  guestCanUnmute = false,
  onGuestCanUnmuteChange,
  muteOnEntry = false,
  onMuteOnEntryChange,
  webinarMode = false,
}) {
  const [activeTab, setActiveTab] = useState('general');
  const [isTesting, setIsTesting] = useState(false);
  const { privacy: privacyMode, isRoom: meetingIsRoom, emitPrivacyUpdate } = useMeetingState();

  // Camera Resolution dropdown is restricted to stage roles. Regular attendees
  // shouldn't be able to override their publish resolution to 1080p — that
  // bandwidth/CPU only makes sense for people whose video is consumed at
  // high quality (host/co-host/presenter on stage, or recorded). 'moderator'
  // is included because that's the value used for "host" in this codebase.
  const canChangeCameraResolution = ['moderator', 'host', 'co-host', 'presenter'].includes(meetingRole);

  // Local state for stats display - only updates when Statistics tab is active
  const [localParticipantStats, setLocalParticipantStats] = useState(null);
  useEffect(() => {
    if (activeTab !== 'statistics' || !localParticipantStatsRef) return;
    // Read the ref immediately
    setLocalParticipantStats(localParticipantStatsRef.current);
    // Poll the ref every 2s only while the Statistics tab is shown
    const interval = setInterval(() => {
      setLocalParticipantStats(localParticipantStatsRef.current);
    }, 2000);
    return () => clearInterval(interval);
  }, [activeTab, localParticipantStatsRef]);

  // Load volume settings from localStorage
  const [inputVolume, setInputVolume] = useState(() => {
    try {
      const saved = localStorage.getItem('rlink_audio_settings');
      if (saved) {
        const audioSettings = JSON.parse(saved);
        return audioSettings.inputVolume ?? 80;
      }
    } catch (e) {}
    return 80;
  });

  const [outputVolume, setOutputVolume] = useState(() => {
    try {
      const saved = localStorage.getItem('rlink_audio_settings');
      if (saved) {
        const audioSettings = JSON.parse(saved);
        return audioSettings.outputVolume ?? 100;
      }
    } catch (e) {}
    return 100;
  });

  // Video preview
  const videoRef = useRef(null);
  // Container for Agora's track.play() — the only reliable way to render the
  // VB-processed (blurred / image-background) preview locally. Agora's VB
  // extension's getProcessedTrack() does not produce a flowing MediaStream
  // via srcObject in this version, so we render via the SDK's player instead.
  const previewAgoraContainerRef = useRef(null);
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const streamRef = useRef(null);
  const [isApplyingEffect, setIsApplyingEffect] = useState(false);

  // Audio level meters
  const [micLevel, setMicLevel] = useState(0);
  const [speakerLevel, setSpeakerLevel] = useState(0);
  const [isMicTesting, setIsMicTesting] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Settings state - load from localStorage
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('rlink_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
      }
    }
    return {
      mirrorVideo: true,
      hdVideo: true,
      touchUpAppearance: false,
      touchUpLevel: 50,
      lowLightAdjust: true,
      noiseSuppression: true,
      echoCancellation: true,
      autoAdjustMic: true,
      muteOnJoin: false,
      cameraOffOnJoin: false,
      colorMode: 'dark',
      cameraResolution: '480p',
    };
  });

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('rlink_settings', JSON.stringify(settings));
  }, [settings]);

  // Save audio volume settings to localStorage
  useEffect(() => {
    const audioSettings = { inputVolume, outputVolume };
    localStorage.setItem('rlink_audio_settings', JSON.stringify(audioSettings));
  }, [inputVolume, outputVolume]);

  // Apply output volume to all audio/video elements
  useEffect(() => {
    const volumeLevel = outputVolume / 100;
    const audioElements = document.querySelectorAll('audio, video');
    audioElements.forEach((element) => {
      element.volume = volumeLevel;
    });
  }, [outputVolume]);

  // Notify parent of audio settings changes (for noise suppression, echo cancellation)
  // NOTE: onAudioSettingsChange is intentionally excluded from deps to prevent
  // re-render loops when the callback reference changes on parent re-render
  const onAudioSettingsChangeRef = useRef(onAudioSettingsChange);
  useEffect(() => { onAudioSettingsChangeRef.current = onAudioSettingsChange; }, [onAudioSettingsChange]);
  useEffect(() => {
    if (onAudioSettingsChangeRef.current) {
      onAudioSettingsChangeRef.current({
        noiseSuppression: settings.noiseSuppression,
        echoCancellation: settings.echoCancellation,
        autoAdjustMic: settings.autoAdjustMic,
        inputVolume,
        outputVolume,
      });
    }
  }, [settings.noiseSuppression, settings.echoCancellation, settings.autoAdjustMic, inputVolume, outputVolume]);

  // Green screen state (local state synced with prop)
  const [greenScreenEnabled, setGreenScreenEnabled] = useState(greenScreenEnabledProp);
  const [greenScreenColor, setGreenScreenColor] = useState('#00ff00');
  const [greenScreenSimilarity, setGreenScreenSimilarity] = useState(40);
  const [greenScreenSmoothness, setGreenScreenSmoothness] = useState(10);
  const [greenScreenBackgroundUrl, setGreenScreenBackgroundUrl] = useState(null);

  // Sync green screen enabled state with prop
  useEffect(() => {
    setGreenScreenEnabled(greenScreenEnabledProp);
  }, [greenScreenEnabledProp]);

  // AI background generator state
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [generatedBackgrounds, setGeneratedBackgrounds] = useState([]);

  // Room background state (S3-backed)
  const customBgInputRef = useRef(null);
  const [roomBackgrounds, setRoomBackgrounds] = useState([]);
  const [isUploadingBg, setIsUploadingBg] = useState(false);
  const [isDeletingBgId, setIsDeletingBgId] = useState(null);

  // Staged virtual-background selection so the user previews a choice in the
  // grid and only pushes it to the live video track when clicking Apply.
  // undefined = nothing staged; { type: 'none' } = staged removal; { type: 'image', url } = staged image.
  const [pendingBackground, setPendingBackground] = useState(undefined);
  useEffect(() => {
    if (activeTab !== 'visual') setPendingBackground(undefined);
  }, [activeTab]);

  const handleApplyPendingBackground = async () => {
    if (pendingBackground === undefined) return;
    if (pendingBackground.type === 'none') {
      await handleRemoveBackground();
    } else if (pendingBackground.type === 'image') {
      await handleSelectBackground(pendingBackground.url);
    }
    setPendingBackground(undefined);
  };

  const isBgStaged = (url) =>
    pendingBackground && pendingBackground.type === 'image' && pendingBackground.url === url;
  const isNoneStaged = pendingBackground && pendingBackground.type === 'none';
  const isApplyDisabled =
    pendingBackground === undefined ||
    isApplyingEffect ||
    (pendingBackground.type === 'none' && !virtualBackgroundOn && !blurEnabled) ||
    (pendingBackground.type === 'image' && selectedBackground === pendingBackground.url);

  // Local-only preview pipeline. Uses a SECOND camera capture + the VideoSDK
  // VirtualBackgroundProcessor (same processor the prelaunch screen uses) to
  // composite the staged background into the modal's preview <video>, without
  // touching the published Agora track. Cleaned up when staging is cleared.
  const previewRawStreamRef = useRef(null);
  const previewProcessorRef = useRef(null);
  const previewProcessedStreamRef = useRef(null);
  const [previewError, setPreviewError] = useState(null);

  const cleanupPreviewPipeline = async () => {
    try {
      if (previewProcessorRef.current) {
        try { await previewProcessorRef.current.stop(); } catch {}
        previewProcessorRef.current = null;
      }
      if (previewProcessedStreamRef.current) {
        previewProcessedStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
        previewProcessedStreamRef.current = null;
      }
      if (previewRawStreamRef.current) {
        previewRawStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
        previewRawStreamRef.current = null;
      }
    } catch (e) {
      console.warn('cleanupPreviewPipeline error:', e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (pendingBackground === undefined) {
        await cleanupPreviewPipeline();
        setPreviewError(null);
        if (videoRef.current && (activeTab === 'visual' || activeTab === 'camera') && isOpen) {
          updatePreviewFromMeetingStream();
        }
        return;
      }

      if (activeTab !== 'visual' || !isOpen) return;

      try {
        // Fresh raw camera stream each time a new staged selection comes in —
        // the processor consumes it, so re-starting needs a new source.
        if (previewProcessorRef.current) {
          try { await previewProcessorRef.current.stop(); } catch {}
          previewProcessorRef.current = null;
        }
        if (previewProcessedStreamRef.current) {
          previewProcessedStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
          previewProcessedStreamRef.current = null;
        }
        if (previewRawStreamRef.current) {
          previewRawStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
          previewRawStreamRef.current = null;
        }

        if (pendingBackground.type === 'none') {
          // Staged "remove background" — just show the raw camera in preview.
          const constraints = {
            video: selectedVideoInput
              ? { deviceId: { exact: selectedVideoInput }, width: 640, height: 360 }
              : { width: 640, height: 360 },
            audio: false,
          };
          const raw = await navigator.mediaDevices.getUserMedia(constraints);
          if (cancelled) { raw.getTracks().forEach((t) => t.stop()); return; }
          previewRawStreamRef.current = raw;
          if (videoRef.current) {
            videoRef.current.srcObject = raw;
            videoRef.current.play().catch(() => {});
            setIsPreviewActive(true);
            setPreviewError(null);
          }
          return;
        }

        if (pendingBackground.type !== 'image') return;

        const constraints = {
          video: selectedVideoInput
            ? { deviceId: { exact: selectedVideoInput }, width: 640, height: 360 }
            : { width: 640, height: 360 },
          audio: false,
        };
        const raw = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) { raw.getTracks().forEach((t) => t.stop()); return; }
        previewRawStreamRef.current = raw;

        const proc = new VirtualBackgroundProcessor();
        previewProcessorRef.current = proc;
        if (!proc.ready) await proc.init();
        if (cancelled) return;

        const processedStream = await proc.start(raw, {
          type: 'image',
          imageUrl: pendingBackground.url,
        });
        if (cancelled) {
          try { await proc.stop(); } catch {}
          return;
        }
        previewProcessedStreamRef.current = processedStream;

        if (videoRef.current && processedStream) {
          videoRef.current.srcObject = processedStream;
          videoRef.current.play().catch(() => {});
          setIsPreviewActive(true);
          setPreviewError(null);
        }
      } catch (err) {
        console.error('preview pipeline failed:', err);
        setPreviewError(err?.message || 'Preview failed');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [pendingBackground, activeTab, isOpen, selectedVideoInput]);

  useEffect(() => () => { cleanupPreviewPipeline(); }, []);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Fetch user's backgrounds when Visual Effects tab is active
  useEffect(() => {
    if (activeTab !== 'visual') return;
    let cancelled = false;
    getRoomVirtualBackgrounds()
      .then(res => {
        if (cancelled) return;
        const backgrounds = res?.data?.data || res?.data;
        if (Array.isArray(backgrounds)) {
          setRoomBackgrounds(backgrounds);
        }
      })
      .catch(err => console.error('[VirtualBG] Fetch failed:', err?.response?.status, err?.response?.data || err.message));
    return () => { cancelled = true; };
  }, [activeTab]);

  // Handle custom background upload to S3
  const handleCustomBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!roomId) {
      console.error('[VirtualBG] roomId is not available, cannot upload');
      return;
    }
    console.log('[VirtualBG] Starting upload', { fileName: file.name, roomId, fileSize: file.size });
    setIsUploadingBg(true);
    try {
      const res = await uploadVirtualBackground(file, roomId);
      const bg = res?.data?.data || res?.data;
      if (bg?._id && bg?.url) {
        setRoomBackgrounds(prev => [bg, ...prev]);
        if (handleStartVirtualBackground) {
          handleStartVirtualBackground({ type: 'image', imageUrl: bg.url });
        }
      } else {
        console.error('[VirtualBG] Unexpected upload response:', res?.data);
      }
    } catch (err) {
      console.error('[VirtualBG] Upload failed:', err?.response?.status, err?.response?.data || err.message);
    } finally {
      setIsUploadingBg(false);
      if (customBgInputRef.current) customBgInputRef.current.value = '';
    }
  };

  // Handle deleting a room background
  const handleDeleteRoomBackground = async (bgId) => {
    setIsDeletingBgId(bgId);
    try {
      await deleteVirtualBackground(bgId);
      const deleted = roomBackgrounds.find(bg => bg._id === bgId);
      setRoomBackgrounds(prev => prev.filter(bg => bg._id !== bgId));
      if (deleted && selectedBackground === deleted.url) {
        if (handleStartVirtualBackground) {
          handleStartVirtualBackground(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete virtual background:', err);
    } finally {
      setIsDeletingBgId(null);
    }
  };

  // Handle AI background generation
  const handleGenerateAIBackground = async () => {
    if (!aiPrompt.trim()) return;

    setIsGeneratingAI(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const response = await fetch(`${baseUrl}/openai/generate-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();

      if (response.ok && data.imageUrl) {
        setGeneratedBackgrounds(prev => [data.imageUrl, ...prev]);
        // Automatically apply the generated background
        if (handleStartVirtualBackground) {
          handleStartVirtualBackground({ type: 'image', imageUrl: data.imageUrl });
        }
        setAiPrompt('');
      } else {
        console.error('Failed to generate AI background:', data.error);
        alert(data.error || 'Failed to generate background. Please try again.');
      }
    } catch (error) {
      console.error('Error generating AI background:', error);
      alert('Failed to generate background. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Handle background selection (image backgrounds)
  const handleSelectBackground = async (bgUrl) => {
    console.log("handleSelectBackground called:", bgUrl);
    setIsApplyingEffect(true);

    try {
      // Apply to main meeting - preview will update via useEffect when stream changes
      if (handleStartVirtualBackground) {
        await handleStartVirtualBackground({ type: 'image', imageUrl: bgUrl });
        // Give time for the stream to update before removing the loading indicator
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    } finally {
      setIsApplyingEffect(false);
    }
  };

  // Handle blur toggle. Do NOT eagerly set blurEnabled here —
  // handleStartVirtualBackground sets it after the processor change commits,
  // so an early setBlurEnabled would create an inconsistent state window that
  // wakes other useEffects mid-mutation.
  const handleBlurToggle = async (enabled) => {
    console.log("handleBlurToggle called:", enabled);
    setIsApplyingEffect(true);

    try {
      if (enabled) {
        // Apply blur to main meeting
        if (handleStartVirtualBackground) {
          await handleStartVirtualBackground({ type: 'blur', blurStrength: blurStrength });
          // Give time for the stream to update
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } else {
        // Remove virtual background entirely
        if (handleStartVirtualBackground) {
          await handleStartVirtualBackground(null);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    } finally {
      setIsApplyingEffect(false);
    }
  };

  // Handle removing virtual background. handleStartVirtualBackground(null)
  // sets blurEnabled=false itself once the processor is fully disabled, so
  // we don't pre-set it here — that would race the async processor work.
  const handleRemoveBackground = async () => {
    console.log("handleRemoveBackground called");
    setIsApplyingEffect(true);

    try {
      // Also disable green screen if it was enabled
      if (greenScreenEnabled && handleStartGreenScreen) {
        await handleStartGreenScreen({ enabled: false });
      }

      // Remove from main meeting
      if (handleStartVirtualBackground) {
        await handleStartVirtualBackground(null);
      }
    } finally {
      setIsApplyingEffect(false);
    }
  };

  // Handle green screen toggle
  const handleGreenScreenToggle = async (enabled) => {
    console.log("handleGreenScreenToggle called:", enabled);
    setIsApplyingEffect(true);

    try {
      if (enabled) {
        // Disable blur and virtual background first
        if (setBlurEnabled) setBlurEnabled(false);

        // Enable green screen
        if (handleStartGreenScreen) {
          await handleStartGreenScreen({
            enabled: true,
            keyColor: greenScreenColor,
            similarity: greenScreenSimilarity,
            smoothness: greenScreenSmoothness,
            backgroundImageUrl: greenScreenBackgroundUrl || selectedBackground,
          });
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } else {
        // Disable green screen
        if (handleStartGreenScreen) {
          await handleStartGreenScreen({ enabled: false });
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      setGreenScreenEnabled(enabled);
    } finally {
      setIsApplyingEffect(false);
    }
  };

  // Handle green screen config changes (when already enabled)
  const handleGreenScreenConfigChange = async (config) => {
    if (!greenScreenEnabled || !handleStartGreenScreen) return;

    console.log("Updating green screen config:", config);
    setIsApplyingEffect(true);

    try {
      await handleStartGreenScreen({
        enabled: true,
        keyColor: config.keyColor || greenScreenColor,
        similarity: config.similarity !== undefined ? config.similarity : greenScreenSimilarity,
        smoothness: config.smoothness !== undefined ? config.smoothness : greenScreenSmoothness,
        backgroundImageUrl: config.backgroundImageUrl || greenScreenBackgroundUrl || selectedBackground,
      });
      await new Promise(resolve => setTimeout(resolve, 200));
    } finally {
      setIsApplyingEffect(false);
    }
  };

  // Handle selecting a background for green screen
  const handleGreenScreenBackgroundSelect = async (bgUrl) => {
    setGreenScreenBackgroundUrl(bgUrl);
    if (greenScreenEnabled && handleStartGreenScreen) {
      setIsApplyingEffect(true);
      try {
        await handleStartGreenScreen({
          enabled: true,
          keyColor: greenScreenColor,
          similarity: greenScreenSimilarity,
          smoothness: greenScreenSmoothness,
          backgroundImageUrl: bgUrl,
        });
        await new Promise(resolve => setTimeout(resolve, 200));
      } finally {
        setIsApplyingEffect(false);
      }
    }
  };

  const handleTestSpeaker = async () => {
    setIsTesting(true);
    // Play test sound using the imported audio file
    const audio = new Audio(testSoundFile);

    // Animate speaker level during playback
    const speakerAnimationInterval = setInterval(() => {
      // Simulate audio levels based on test sound
      const level = Math.random() * 60 + 20; // Random level between 20-80%
      setSpeakerLevel(level);
    }, 100);

    const cleanup = () => {
      clearInterval(speakerAnimationInterval);
      setSpeakerLevel(0);
      setIsTesting(false);
    };

    try {
      // Route audio to selected speaker device if supported
      if (selectedAudioOutput && audio.setSinkId) {
        await audio.setSinkId(selectedAudioOutput);
      }
      await audio.play();
      audio.onended = cleanup;
    } catch (error) {
      console.error('Failed to play test sound:', error);
      // Try playing without setSinkId as fallback
      try {
        await audio.play();
        audio.onended = cleanup;
      } catch (fallbackError) {
        console.error('Fallback play also failed:', fallbackError);
        cleanup();
      }
    }

    // Safety timeout in case onended doesn't fire
    setTimeout(cleanup, 3000);
  };

  // Start microphone testing with audio level analysis
  const startMicTest = async () => {
    if (isMicTesting) {
      stopMicTest();
      return;
    }

    try {
      setIsMicTesting(true);

      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedAudioInput ? { exact: selectedAudioInput } : undefined,
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
        }
      });
      micStreamRef.current = stream;

      // Create audio context and analyzer
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Start analyzing audio levels
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const updateLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const average = sum / dataArray.length;
        // Normalize to 0-100 scale
        const normalizedLevel = Math.min(100, (average / 128) * 100);
        setMicLevel(normalizedLevel);

        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
    } catch (error) {
      console.error('Error starting mic test:', error);
      setIsMicTesting(false);
    }
  };

  // Stop microphone testing
  const stopMicTest = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setMicLevel(0);
    setIsMicTesting(false);
  };

  // Cleanup on unmount or tab change
  useEffect(() => {
    return () => {
      stopMicTest();
    };
  }, []);

  // Stop mic test when switching away from audio tab
  useEffect(() => {
    if (activeTab !== 'audio') {
      stopMicTest();
    }
  }, [activeTab]);

  // Use the processed webcam stream (with effects applied) if available
  // Otherwise fall back to the raw meeting stream or a separate camera stream
  const updatePreviewFromMeetingStream = () => {
    if (!videoRef.current) {
      console.log("updatePreviewFromMeetingStream: No video ref available");
      return false;
    }

    try {
      // Priority 1: Use the processed stream with virtual background effects
      // processedWebcamStream is already a MediaStream from videoProcessor.start()
      if (processedWebcamStream) {
        console.log("Using processedWebcamStream for preview", processedWebcamStream);
        // Use the MediaStream directly - no need to wrap it
        videoRef.current.srcObject = processedWebcamStream;
        videoRef.current.play().catch(err => console.error('Failed to play preview:', err));
        setIsPreviewActive(true);
        console.log("Preview updated with processed stream (with effects)");
        return true;
      }

      // Priority 2: Use the meeting's webcam stream (raw, no effects)
      if (localWebcamStream) {
        const track = localWebcamStream?.track;
        if (track) {
          const mediaStream = new MediaStream([track]);
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(err => console.error('Failed to play preview:', err));
          setIsPreviewActive(true);
          console.log("Using meeting webcam stream for preview (raw)");
          return true;
        }
      }

      console.log("updatePreviewFromMeetingStream: No stream available");
      return false;
    } catch (error) {
      console.error('updatePreviewFromMeetingStream failed:', error);
      return false;
    }
  };

  // Start camera preview - prefer processed stream (has effects), then meeting stream, then fallback
  const startCameraPreview = async () => {
    try {
      // First try to use the processed stream or meeting's webcam stream
      if ((localWebcamOn || processedWebcamStream) && updatePreviewFromMeetingStream()) {
        return;
      }

      // Stop any existing separate preview stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Fallback: Request a new camera stream for preview (raw, no effects)
      const constraints = {
        video: selectedVideoInput
          ? { deviceId: { exact: selectedVideoInput }, width: 640, height: 480 }
          : { width: 640, height: 480 },
        audio: false
      };

      console.log("Starting fallback camera preview");
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsPreviewActive(true);
      }
    } catch (err) {
      console.error('Failed to start camera preview:', err);
      setIsPreviewActive(false);
    }
  };

  const stopCameraPreview = () => {
    // Only stop the separate preview stream, not the meeting camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsPreviewActive(false);
  };

  // Update preview when meeting webcam stream or processed stream changes (e.g., when effect is applied)
  useEffect(() => {
    // While a staged-background preview is showing, the dedicated preview
    // pipeline above owns videoRef — don't overwrite it from the live stream.
    if (pendingBackground !== undefined) return;
    if ((activeTab === 'visual' || activeTab === 'camera') && isOpen && localWebcamOn) {
      // Add a small delay to ensure the processed stream is ready after effect changes
      const timeoutId = setTimeout(() => {
        console.log("Updating preview after effect change...", {
          virtualBackgroundOn,
          blurEnabled,
          hasProcessedStream: !!processedWebcamStream,
          hasStream: !!localWebcamStream,
          hasTrack: !!localWebcamStream?.track
        });
        updatePreviewFromMeetingStream();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [localWebcamStream, localWebcamOn, virtualBackgroundOn, blurEnabled, selectedBackground, processedWebcamStream]);

  // When blur or a virtual-background image is active, render the live preview
  // via Agora's track.play() into a dedicated container. The srcObject path
  // above can't show the processed (blurred/composited) frames because
  // Agora's VB extension routes processed frames to processorDestination, not
  // through getMediaStreamTrack(). track.play() is the SDK-supported way to
  // see the same output remote viewers see.
  //
  // Side effect: track.play() detaches the track from any previous container
  // (the local participant tile). When the modal closes — or the effect goes
  // off — we dispatch 'agora-preview-detached' so AgoraVideoPlayer in the
  // grid can re-claim the track.
  const useAgoraPreview =
    (blurEnabled || virtualBackgroundOn) &&
    localWebcamOn &&
    pendingBackground === undefined &&
    !!localWebcamStream?.agoraTrack;

  useEffect(() => {
    if (activeTab !== 'visual' || !isOpen || !useAgoraPreview) return;
    const container = previewAgoraContainerRef.current;
    const agoraTrack = localWebcamStream?.agoraTrack;
    if (!container || !agoraTrack || typeof agoraTrack.play !== 'function') return;

    try {
      container.innerHTML = '';
      agoraTrack.play(container, {
        mirror: !!settings.mirrorVideo,
        fit: 'contain',
      });
    } catch (err) {
      console.warn('[SettingsModal] agoraTrack.play() failed for preview:', err);
    }

    return () => {
      if (container) container.innerHTML = '';
      // Notify other AgoraVideoPlayer instances (grid tile) to reclaim the
      // track now that we've released our hold on it.
      try {
        window.dispatchEvent(new CustomEvent('agora-preview-detached'));
      } catch (_) { /* ignore */ }
    };
  }, [activeTab, isOpen, useAgoraPreview, localWebcamStream, settings.mirrorVideo]);

  // Start/stop preview based on tab and modal state
  useEffect(() => {
    if ((activeTab === 'visual' || activeTab === 'camera') && isOpen) {
      startCameraPreview();
    } else {
      stopCameraPreview();
    }

    return () => {
      // Only stop separate stream on unmount, not meeting stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [activeTab, isOpen, selectedVideoInput]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl h-[650px] bg-[#0a0a1a] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
        <div className="flex h-full">
          {/* Sidebar Navigation */}
          <div className="w-52 bg-[#001233] border-r border-white/10 p-2">
            <div className="flex items-center justify-between px-3 py-2 mb-2">
              <h2 className="text-sm font-semibold text-white">Settings</h2>
            </div>
            <nav className="space-y-1">
              {SETTINGS_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'bg-[#6a1fbf] text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">
                {SETTINGS_TABS.find(t => t.id === activeTab)?.label}
              </h3>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'general' && (
                <div className="space-y-6">
                  {/* Appearance Section - hidden for now */}
                  {false && <section>
                    <h4 className="text-sm text-gray-400 mb-3">Appearance</h4>
                    <div className="inline-flex bg-[#1a1a2e] rounded-full p-1">
                      {[
                        { id: 'auto', label: 'Auto', icon: '◐' },
                        { id: 'light', label: 'Light', icon: '☀' },
                        { id: 'dark', label: 'Dark', icon: '☾' },
                      ].map((mode) => (
                        <button
                          key={mode.id}
                          onClick={() => updateSetting('colorMode', mode.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                            settings.colorMode === mode.id
                              ? 'bg-[#2a2a4a] text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          <span className="text-base">{mode.icon}</span>
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  </section>}

                  {/* Join Settings */}
                  <section>
                    <h4 className="text-base font-medium text-white mb-4">When Joining a Meeting</h4>
                    <div className="bg-white/5 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white">Mute my microphone</p>
                          <p className="text-xs text-gray-500">Start with microphone muted</p>
                        </div>
                        <ToggleSwitch
                          checked={settings.muteOnJoin}
                          onChange={(v) => updateSetting('muteOnJoin', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white">Turn off my camera</p>
                          <p className="text-xs text-gray-500">Start with camera turned off</p>
                        </div>
                        <ToggleSwitch
                          checked={settings.cameraOffOnJoin}
                          onChange={(v) => updateSetting('cameraOffOnJoin', v)}
                        />
                      </div>
                    </div>
                  </section>

                  {/* Host Controls - only for moderator/co-host, non-webinar */}
                  {['moderator', 'co-host'].includes(meetingRole) && !webinarMode && (
                    <section>
                      <h4 className="text-base font-medium text-white mb-4">Host Controls</h4>
                      <div className="bg-white/5 rounded-xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">Enable Waiting Room</p>
                            <p className="text-xs text-gray-500">Require host approval before participants can join</p>
                          </div>
                          <ToggleSwitch
                            checked={waitingRoomEnabled}
                            onChange={onWaitingRoomChange}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">Auto-admit registered users</p>
                            <p className="text-xs text-gray-500">Skip waiting room for registered attendees</p>
                          </div>
                          <ToggleSwitch
                            checked={autoAdmitRegistered}
                            onChange={onAutoAdmitRegisteredChange}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">Enable Guest Unmute</p>
                            <p className="text-xs text-gray-500">Allow guests to unmute themselves</p>
                          </div>
                          <ToggleSwitch
                            checked={guestCanUnmute}
                            onChange={onGuestCanUnmuteChange}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">Mute Participants on Entry</p>
                            <p className="text-xs text-gray-500">New participants will join muted. Hosts can unmute them later.</p>
                          </div>
                          <ToggleSwitch
                            checked={muteOnEntry}
                            onChange={onMuteOnEntryChange}
                          />
                        </div>
                        {meetingIsRoom && (
                          <div className="flex items-center justify-between" data-testid="settings-privacy-row">
                            <div>
                              <p className="text-sm text-white">Privacy Mode</p>
                              <p className="text-xs text-gray-500">Privacy mode is enabled. Recording, screenshots, and screen capture are disabled to protect this meeting.</p>
                            </div>
                            <ToggleSwitch
                              checked={!!privacyMode}
                              onChange={(v) => emitPrivacyUpdate?.(!!v)}
                            />
                          </div>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              )}

              {activeTab === 'camera' && (
                <div className="space-y-4">
                  {/* Camera Preview */}
                  <div className="bg-black rounded-lg overflow-hidden border border-white/10 h-[280px] relative">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-contain ${settings.mirrorVideo ? 'scale-x-[-1]' : ''}`}
                    />
                    {!isPreviewActive && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                        <Video className="w-10 h-10 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Camera & Resolution */}
                  <div className={canChangeCameraResolution ? "grid grid-cols-2 gap-3" : ""}>
                    <div className="bg-white/5 rounded-lg p-3">
                      <label className="text-xs text-gray-400 mb-1.5 block">Camera</label>
                      <div className="relative">
                        <select
                          value={selectedVideoInput || ''}
                          onChange={(e) => onVideoInputChange?.(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
                        >
                          {videoInputs.map(device => (
                            <option key={device.deviceId} value={device.deviceId} className="bg-[#0a0a1a]">
                              {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    {canChangeCameraResolution && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <label className="text-xs text-gray-400 mb-1.5 block">Resolution</label>
                        <div className="relative">
                          <select
                            value={settings.cameraResolution}
                            onChange={(e) => {
                              const newResolution = e.target.value;
                              updateSetting('cameraResolution', newResolution);
                              // Apply resolution change to the meeting
                              if (onCameraResolutionChange) {
                                onCameraResolutionChange(newResolution);
                              }
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
                          >
                            <option value="480p" className="bg-[#0a0a1a]">480p</option>
                            <option value="720p" className="bg-[#0a0a1a]">720p</option>
                            <option value="1080p" className="bg-[#0a0a1a]">1080p</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mirror Video */}
                  <div className="bg-white/5 rounded-lg p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.mirrorVideo}
                        onChange={(e) => updateSetting('mirrorVideo', e.target.checked)}
                        className="w-4 h-4 rounded border-white/30 bg-transparent accent-[#6a1fbf]"
                      />
                      <span className="text-sm text-white">Mirror my camera</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'visual' && (
                <div className="space-y-4">
                  {/* Video Preview */}
                  <div className="bg-black rounded-lg overflow-hidden border border-white/10 h-[180px] relative">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-contain ${settings.mirrorVideo ? 'scale-x-[-1]' : ''}`}
                      style={useAgoraPreview ? { display: 'none' } : undefined}
                    />
                    {/* Agora SDK player target — used when blur/VB is active. */}
                    <div
                      ref={previewAgoraContainerRef}
                      className="absolute inset-0 w-full h-full"
                      style={{ display: useAgoraPreview ? 'block' : 'none' }}
                    />
                    {!isPreviewActive && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                        <Video className="w-10 h-10 text-gray-600" />
                      </div>
                    )}
                    {isApplyingEffect && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                      </div>
                    )}
                    {/* Effect indicator - shows what's active in the meeting */}
                    {(blurEnabled || selectedBackground) && (
                      <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-xs text-white flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-purple-400" />
                        {blurEnabled ? 'Blur Active' : 'Background Active'}
                      </div>
                    )}
                    {/* Preview source indicator */}
                    <div className="absolute top-2 right-2 bg-black/60 px-2 py-1 rounded text-[10px] text-gray-400">
                      {localWebcamOn && (blurEnabled || selectedBackground) ? 'Live Preview' : 'Camera Preview'}
                    </div>
                  </div>

                  {/* Virtual Backgrounds Section */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                      <Image className="w-4 h-4 text-purple-400" />
                      Virtual Backgrounds
                    </h4>

                    {/* Blur Controls */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">Background Blur</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleBlurToggle(false)}
                            className={`px-3 py-1 text-xs rounded-l-lg transition-colors ${
                              !blurEnabled
                                ? 'bg-[#6a1fbf] text-white'
                                : 'bg-white/10 text-gray-400 hover:bg-white/20'
                            }`}
                          >
                            Off
                          </button>
                          <button
                            onClick={() => handleBlurToggle(true)}
                            className={`px-3 py-1 text-xs rounded-r-lg transition-colors ${
                              blurEnabled
                                ? 'bg-[#6a1fbf] text-white'
                                : 'bg-white/10 text-gray-400 hover:bg-white/20'
                            }`}
                          >
                            On
                          </button>
                        </div>
                      </div>
                      {blurEnabled && (
                        <div className="mt-2">
                          <label className="text-xs text-gray-400 mb-1 block">
                            Blur Strength: {blurStrength}%
                          </label>
                          <Slider
                            value={blurStrength}
                            onChange={(v) => setBlurStrength?.(v)}
                            min={10}
                            max={100}
                            step={5}
                          />
                        </div>
                      )}
                    </div>

                    {/* Background Selection */}
                    <div className="mb-3">
                      <span className="text-xs text-gray-400 block mb-2">Select Background</span>
                      <div className="grid grid-cols-5 gap-2 max-h-[120px] overflow-y-auto pr-1">
                        {/* No Background Option */}
                        <button
                          onClick={() => setPendingBackground({ type: 'none' })}
                          className={`aspect-video rounded-lg border-2 transition-all flex items-center justify-center bg-gray-800 ${
                            isNoneStaged
                              ? 'border-dashed border-[#00c853] ring-2 ring-[#00c853]/60'
                              : !virtualBackgroundOn && !blurEnabled && pendingBackground === undefined
                              ? 'border-[#6a1fbf] ring-2 ring-[#6a1fbf]/50'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>

                        {/* Room backgrounds (user's own uploads) */}
                        {roomBackgrounds.map((bg) => (
                          <div
                            key={bg._id}
                            className="relative group"
                          >
                            <button
                              onClick={() => setPendingBackground({ type: 'image', url: bg.url })}
                              className={`w-full aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                                isBgStaged(bg.url)
                                  ? 'border-dashed border-[#00c853] ring-2 ring-[#00c853]/60'
                                  : selectedBackground === bg.url && pendingBackground === undefined
                                  ? 'border-[#6a1fbf] ring-2 ring-[#6a1fbf]/50'
                                  : 'border-white/10 hover:border-white/30'
                              }`}
                            >
                              <img src={bg.url} alt={bg.originalName || 'Custom'} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteRoomBackground(bg._id); }}
                              disabled={isDeletingBgId === bg._id}
                              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Delete background"
                            >
                              {isDeletingBgId === bg._id
                                ? <Loader2 className="w-3 h-3 text-white animate-spin" />
                                : <X className="w-3 h-3 text-white" />
                              }
                            </button>
                          </div>
                        ))}

                        {/* AI generated backgrounds */}
                        {generatedBackgrounds.map((bg, index) => (
                          <button
                            key={`ai-${index}`}
                            onClick={() => setPendingBackground({ type: 'image', url: bg })}
                            className={`aspect-video rounded-lg border-2 transition-all overflow-hidden relative ${
                              isBgStaged(bg)
                                ? 'border-dashed border-[#00c853] ring-2 ring-[#00c853]/60'
                                : selectedBackground === bg && pendingBackground === undefined
                                ? 'border-[#6a1fbf] ring-2 ring-[#6a1fbf]/50'
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <img src={bg} alt="AI Generated" className="w-full h-full object-cover" />
                            <Wand2 className="absolute top-1 right-1 w-3 h-3 text-purple-400" />
                          </button>
                        ))}

                        {/* Preset backgrounds */}
                        {backgroundImageUrls.map((bg, index) => (
                          <button
                            key={index}
                            onClick={() => setPendingBackground({ type: 'image', url: bg })}
                            className={`aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                              isBgStaged(bg)
                                ? 'border-dashed border-[#00c853] ring-2 ring-[#00c853]/60'
                                : selectedBackground === bg && pendingBackground === undefined
                                ? 'border-[#6a1fbf] ring-2 ring-[#6a1fbf]/50'
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <img
                              src={bg}
                              alt={`Background ${index + 1}`}
                              className="w-full h-full object-cover"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<span class="text-[10px] text-gray-500">Image ${index + 1}</span>`;
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Apply / Cancel staged background */}
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        onClick={handleApplyPendingBackground}
                        disabled={isApplyDisabled}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-sm text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isApplyingEffect ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        {pendingBackground === undefined
                          ? 'Apply Background'
                          : pendingBackground.type === 'none'
                          ? 'Apply: Remove Background'
                          : 'Apply Background'}
                      </button>
                      {pendingBackground !== undefined && (
                        <button
                          onClick={() => setPendingBackground(undefined)}
                          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-300 hover:bg-white/10 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    {pendingBackground !== undefined && (
                      <p className="text-[11px] text-[#00c853] mb-3 -mt-1">
                        {previewError
                          ? `Preview unavailable (${previewError}). Apply will still work.`
                          : 'Previewing locally — click Apply to push to the live video.'}
                      </p>
                    )}

                    {/* Upload Custom Background */}
                    <div className="flex gap-2">
                      <input
                        ref={customBgInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleCustomBackgroundUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => customBgInputRef.current?.click()}
                        disabled={isUploadingBg}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploadingBg ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Image
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* AI Background Generator Section */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-purple-400" />
                      AI Background Generator
                      <span className="text-[10px] bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full">Beta</span>
                    </h4>
                    <p className="text-xs text-gray-400 mb-3">
                      Describe the background you want and AI will generate it
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="e.g., Modern office with city view"
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf]"
                        onKeyDown={(e) => e.key === 'Enter' && handleGenerateAIBackground()}
                      />
                      <button
                        onClick={handleGenerateAIBackground}
                        disabled={isGeneratingAI || !aiPrompt.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-sm text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isGeneratingAI ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wand2 className="w-4 h-4" />
                        )}
                        Generate
                      </button>
                    </div>
                  </div>

                  {/* Skin Enhancement */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.touchUpAppearance}
                        onChange={(e) => updateSetting('touchUpAppearance', e.target.checked)}
                        className="w-4 h-4 rounded border-white/30 bg-transparent accent-[#6a1fbf]"
                      />
                      <span className="text-sm text-white">Enhance skin appearance</span>
                      <Sparkles className="w-4 h-4 text-purple-400" />
                    </label>

                    {settings.touchUpAppearance && (
                      <div className="mt-4 pl-7 space-y-3">
                        <div>
                          <label className="text-xs text-gray-400 mb-2 block">
                            Intensity: {settings.touchUpLevel}%
                          </label>
                          <Slider
                            value={settings.touchUpLevel}
                            onChange={(v) => updateSetting('touchUpLevel', v)}
                            min={10}
                            max={100}
                            step={5}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Low Light Adjust */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white">Adjust for low light</p>
                        <p className="text-xs text-gray-500">Automatically brighten video in dark environments</p>
                      </div>
                      <ToggleSwitch
                        checked={settings.lowLightAdjust}
                        onChange={(v) => updateSetting('lowLightAdjust', v)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'audio' && (
                <div className="space-y-6">
                  {/* Speaker Section */}
                  <section>
                    <h4 className="text-base font-medium text-white mb-4">Speaker</h4>
                    <div className="bg-white/5 rounded-xl p-4 space-y-4">
                      <div className="relative">
                        <select
                          value={selectedAudioOutput || ''}
                          onChange={(e) => onAudioOutputChange?.(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
                        >
                          {audioOutputs.map(device => (
                            <option key={device.deviceId} value={device.deviceId} className="bg-[#0a0a1a]">
                              {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleTestSpeaker}
                          disabled={isTesting}
                          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {isTesting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                          Test speaker
                        </button>
                        <div className="flex-1 flex items-center gap-1 bg-white/5 rounded-lg px-2 h-8">
                          {[...Array(8)].map((_, i) => {
                            const threshold = ((i + 1) / 8) * 100;
                            const isActive = speakerLevel >= threshold;
                            return (
                              <div
                                key={i}
                                className={`flex-1 h-3 rounded-sm transition-colors ${
                                  isActive
                                    ? i < 5 ? 'bg-green-500' : i < 7 ? 'bg-yellow-500' : 'bg-red-500'
                                    : 'bg-gray-600'
                                }`}
                              />
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-gray-400 mb-2 block">Output volume</label>
                        <div className="flex items-center gap-3">
                          <Volume2 className="w-4 h-4 text-gray-400" />
                          <div className="flex-1">
                            <Slider
                              value={outputVolume}
                              onChange={setOutputVolume}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-8">{outputVolume}%</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Microphone Section */}
                  <section>
                    <h4 className="text-base font-medium text-white mb-4">Microphone</h4>
                    <div className="bg-white/5 rounded-xl p-4 space-y-4">
                      <div className="relative">
                        <select
                          value={selectedAudioInput || ''}
                          onChange={(e) => onAudioInputChange?.(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
                        >
                          {audioInputs.map(device => (
                            <option key={device.deviceId} value={device.deviceId} className="bg-[#0a0a1a]">
                              {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={startMicTest}
                          className={`px-3 py-2 border rounded-lg text-white text-sm transition-colors flex items-center gap-2 ${
                            isMicTesting
                              ? 'bg-red-500/20 border-red-500/50 hover:bg-red-500/30'
                              : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <Mic className={`w-4 h-4 ${isMicTesting ? 'text-red-400' : ''}`} />
                          {isMicTesting ? 'Stop' : 'Test microphone'}
                        </button>
                        <div className="flex-1 flex items-center gap-1 bg-white/5 rounded-lg px-2 h-8">
                          {[...Array(8)].map((_, i) => {
                            const threshold = ((i + 1) / 8) * 100;
                            const isActive = micLevel >= threshold;
                            return (
                              <div
                                key={i}
                                className={`flex-1 h-3 rounded-sm transition-colors ${
                                  isActive
                                    ? i < 5 ? 'bg-green-500' : i < 7 ? 'bg-yellow-500' : 'bg-red-500'
                                    : 'bg-gray-600'
                                }`}
                              />
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-gray-400 mb-2 block">Input volume</label>
                        <div className="flex items-center gap-3">
                          <Mic className="w-4 h-4 text-gray-400" />
                          <div className="flex-1">
                            <Slider
                              value={inputVolume}
                              onChange={setInputVolume}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-8">{inputVolume}%</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div>
                          <p className="text-sm text-white">Automatically adjust microphone volume</p>
                        </div>
                        <ToggleSwitch
                          checked={settings.autoAdjustMic}
                          onChange={(v) => updateSetting('autoAdjustMic', v)}
                        />
                      </div>
                    </div>
                  </section>

                  {/* Audio Processing */}
                  <section>
                    <h4 className="text-base font-medium text-white mb-4">Audio Processing</h4>
                    <div className="bg-white/5 rounded-xl p-4 space-y-4">
                      {/* AI Noise Cancellation */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-sm text-white flex items-center gap-2">
                              AI Noise Cancellation
                              <span className="text-[10px] bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full">AI</span>
                            </p>
                            <p className="text-xs text-gray-500">Remove background noise using AI</p>
                          </div>
                        </div>
                        <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                          <button
                            onClick={() => {
                              onNoiseCancellationToggle?.(false);
                              updateSetting('noiseSuppression', false);
                            }}
                            className={`flex-1 px-3 py-2 text-xs rounded-md transition-colors ${
                              !noiseCancellationEnabled
                                ? 'bg-[#6a1fbf] text-white'
                                : 'text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            Off
                          </button>
                          <button
                            onClick={() => {
                              onNoiseCancellationToggle?.(true, 'STATIONARY_NS', 'SOFT');
                              updateSetting('noiseSuppression', true);
                            }}
                            className={`flex-1 px-3 py-2 text-xs rounded-md transition-colors ${
                              noiseCancellationEnabled && noiseCancellationMode === 'STATIONARY_NS'
                                ? 'bg-[#6a1fbf] text-white'
                                : 'text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            Low
                          </button>
                          <button
                            onClick={() => {
                              onNoiseCancellationToggle?.(true, 'NSNG', 'AGGRESSIVE');
                              updateSetting('noiseSuppression', true);
                            }}
                            className={`flex-1 px-3 py-2 text-xs rounded-md transition-colors ${
                              noiseCancellationEnabled && noiseCancellationMode === 'NSNG'
                                ? 'bg-[#6a1fbf] text-white'
                                : 'text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            High (AI)
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-2">
                          {!noiseCancellationEnabled && 'No noise reduction applied'}
                          {noiseCancellationEnabled && noiseCancellationMode === 'STATIONARY_NS' && 'Reduces stationary noise (fans, AC, etc.)'}
                          {noiseCancellationEnabled && noiseCancellationMode === 'NSNG' && 'AI removes all non-speech sounds for best quality'}
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white">Echo cancellation</p>
                          <p className="text-xs text-gray-500">Reduce echo from speakers</p>
                        </div>
                        <ToggleSwitch
                          checked={settings.echoCancellation}
                          onChange={(v) => updateSetting('echoCancellation', v)}
                        />
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'shortcuts' && (
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4 space-y-3">
                    {[
                      { action: 'Mute/Unmute', shortcut: 'Alt + A' },
                      { action: 'Start/Stop Video', shortcut: 'Alt + V' },
                      { action: 'Start/Stop Screen Share', shortcut: 'Alt + S' },
                      { action: 'Raise/Lower Hand', shortcut: 'Alt + Y' },
                      { action: 'Toggle Chat', shortcut: 'Alt + H' },
                      { action: 'Toggle Participants', shortcut: 'Alt + U' },
                      { action: 'Leave Meeting', shortcut: 'Alt + Q' },
                    ].map((item) => (
                      <div key={item.action} className="flex items-center justify-between py-2">
                        <span className="text-sm text-white">{item.action}</span>
                        <span className="text-xs bg-white/10 px-2 py-1 rounded text-gray-300">{item.shortcut}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'stats' && (
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4 space-y-4">
                    <h4 className="text-sm font-medium text-white">Connection Quality</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Latency</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.rtt || localParticipantStats?.audio?.rtt || '--'} ms
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Packet Loss</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.packetsLost !== undefined
                            ? `${((localParticipantStats.video.packetsLost / (localParticipantStats.video.packetsSent || 1)) * 100).toFixed(2)}%`
                            : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Jitter</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.jitter !== undefined
                            ? `${parseFloat(localParticipantStats.video.jitter).toFixed(2)} ms`
                            : localParticipantStats?.audio?.jitter !== undefined
                            ? `${parseFloat(localParticipantStats.audio.jitter).toFixed(2)} ms`
                            : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Connection</p>
                        <p className="text-lg text-white flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            localParticipantStats?.video?.rtt < 100 ? 'bg-green-500' :
                            localParticipantStats?.video?.rtt < 300 ? 'bg-yellow-500' : 'bg-red-500'
                          }`} />
                          {localParticipantStats?.video?.rtt < 100 ? 'Excellent' :
                           localParticipantStats?.video?.rtt < 300 ? 'Good' :
                           localParticipantStats?.video?.rtt ? 'Poor' : 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 space-y-4">
                    <h4 className="text-sm font-medium text-white">Video Statistics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Resolution (Send)</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.width && localParticipantStats?.video?.height
                            ? `${localParticipantStats.video.width}x${localParticipantStats.video.height}`
                            : settings.cameraResolution === '1080p' ? '1920x1080' :
                              settings.cameraResolution === '720p' ? '1280x720' : '854x480'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Frame Rate</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.framesPerSecond
                            ? `${Math.round(localParticipantStats.video.framesPerSecond)} fps`
                            : '30 fps'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Codec</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.codec || 'VP8'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Bitrate</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.video?.bitrate
                            ? `${(localParticipantStats.video.bitrate / 1000000).toFixed(2)} Mbps`
                            : '-- Mbps'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 space-y-4">
                    <h4 className="text-sm font-medium text-white">Audio Statistics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Codec</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.audio?.codec || 'Opus'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Bitrate</p>
                        <p className="text-lg text-white">
                          {localParticipantStats?.audio?.bitrate
                            ? `${(localParticipantStats.audio.bitrate / 1000).toFixed(0)} kbps`
                            : '-- kbps'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Sample Rate</p>
                        <p className="text-lg text-white">48 kHz</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Channels</p>
                        <p className="text-lg text-white">Stereo</p>
                      </div>
                    </div>
                  </div>

                  {!localParticipantStats && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-xs text-yellow-400">
                        Statistics will be available once you join the meeting with camera/microphone enabled.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
